import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, 
  LineChart, Line, CartesianGrid, PieChart, Pie, Cell, AreaChart, Area,
  Legend, ReferenceLine
} from 'recharts';
import { toast } from '../lib/toast';
import Sidebar from '../components/layout/Sidebar';
import { 
  BarChart3, TrendingUp, TrendingDown, DollarSign, ShoppingBag, Users, 
  Package, Utensils, Truck, RefreshCw, Calendar, Download, X, Phone, Heart,
  Target, AlertTriangle, CheckCircle, Plus, Trash2, Edit, Building2, Zap,
  PiggyBank, Calculator, ChevronRight, ArrowUp, ArrowDown, Flame, Lightbulb
} from 'lucide-react';

const COLORS = ['#f97316', '#10b981', '#6366f1', '#ec4899', '#94a3b8'];
const CHART_COLORS = { sales: '#10b981', costs: '#ef4444', fixed: '#f97316', profit: '#3b82f6' };

const Reports = () => {
  const { api } = useAuth();
  const { currentBranch, isMatriz } = useBranch();
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [topProductsPeriod, setTopProductsPeriod] = useState('all'); // NEW: Filter for top products
  const [topProductsCustomStart, setTopProductsCustomStart] = useState(''); // Custom date range start
  const [topProductsCustomEnd, setTopProductsCustomEnd] = useState(''); // Custom date range end
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false); // Show/hide custom date inputs
  const [topProductsSelectedMonth, setTopProductsSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [salesByType, setSalesByType] = useState([]);
  const [categorySales, setCategorySales] = useState([]);
  const [hourlySales, setHourlySales] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customerReport, setCustomerReport] = useState([]);
  const [timeRange, setTimeRange] = useState('7');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general'); // general, customers, finances
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Finance states
  const [financePeriod, setFinancePeriod] = useState('month');
  const [financeDate, setFinanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [indicators, setIndicators] = useState(null);
  const [trends, setTrends] = useState([]);
  const [productProfitability, setProductProfitability] = useState([]);
  const [fixedExpenses, setFixedExpenses] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [wasteReport, setWasteReport] = useState(null);
  const [areaExpenses, setAreaExpenses] = useState(null);
  const [loadingFinance, setLoadingFinance] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseForm, setExpenseForm] = useState({
    category: '',
    name: '',
    amount: '',
    is_recurring: true,
    recurrence_period: 'monthly',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [timeRange, currentBranch]);

  useEffect(() => {
    if (activeTab === 'finances') {
      fetchFinanceData();
    }
  }, [activeTab, financePeriod, financeDate, currentBranch]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Build branch filter params
      const branchParams = currentBranch 
        ? `&branch_id=${currentBranch.branch_id}&is_matriz=${isMatriz}` 
        : '';
      
      const [statsRes, chartRes, topRes, ordersRes, typeRes, categoryRes, hourlyRes, customerRes] = await Promise.all([
        api.get(`/dashboard/stats?t=${Date.now()}${branchParams}`),
        api.get(`/dashboard/sales-chart?days=${timeRange}${branchParams}`),
        api.get(`/dashboard/top-products?limit=1000${branchParams}`),
        api.get(`/orders?branch_id=${currentBranch?.branch_id || ''}&is_matriz=${isMatriz}`),
        api.get(`/dashboard/sales-by-type?days=${timeRange}${branchParams}`),
        api.get(`/dashboard/category-sales?days=${timeRange}${branchParams}`),
        api.get(`/dashboard/hourly-sales?${branchParams.replace('&', '')}`),
        api.get(`/dashboard/customer-report?days=${timeRange}${branchParams}`)
      ]);
      setStats(statsRes.data);
      setChartData(chartRes.data);
      setTopProducts(topRes.data);
      setOrders(ordersRes.data);
      setSalesByType(typeRes.data);
      setCategorySales(categoryRes.data);
      setHourlySales(hourlyRes.data);
      setCustomerReport(customerRes.data);
    } catch (error) {
      toast.error('Error al cargar reportes');
    } finally {
      setLoading(false);
    }
  };

  // NEW: Fetch top products with period filter or custom date range
  const [loadingTopProducts, setLoadingTopProducts] = useState(false);
  
  // Helper: Generar lista de últimos 12 meses
  const getMonthOptions = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
      months.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return months;
  };

  // Helper: Obtener etiqueta del período actual
  const getTopProductsPeriodLabel = () => {
    const now = new Date();
    switch (topProductsPeriod) {
      case 'today':
        return `Hoy: ${now.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}`;
      case 'week':
        const dayOfWeek = now.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + mondayOffset);
        return `Semana: ${monday.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })} - ${now.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}`;
      case 'month':
        return now.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
      case 'month-specific':
        const [year, month] = topProductsSelectedMonth.split('-').map(Number);
        const selectedDate = new Date(year, month - 1, 1);
        return selectedDate.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
      case 'all':
        return 'Todo el historial';
      case 'custom':
        if (topProductsCustomStart && topProductsCustomEnd) {
          return `${new Date(topProductsCustomStart).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })} - ${new Date(topProductsCustomEnd).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}`;
        }
        return 'Personalizado';
      default:
        return 'Todo el historial';
    }
  };

  const fetchTopProducts = async (period = 'all', startDate = null, endDate = null) => {
    setLoadingTopProducts(true);
    try {
      const branchParams = currentBranch 
        ? `&branch_id=${currentBranch.branch_id}&is_matriz=${isMatriz}` 
        : '';
      
      let url = `/dashboard/top-products?limit=1000${branchParams}`;
      
      if (startDate && endDate) {
        // Custom date range
        url += `&start_date=${startDate}&end_date=${endDate}`;
      } else {
        // Predefined period
        url += `&period=${period}`;
      }
      
      const res = await api.get(url);
      setTopProducts(res.data);
    } catch (error) {
      console.error('Error fetching top products:', error);
      toast.error('Error al cargar productos');
    } finally {
      setLoadingTopProducts(false);
    }
  };

  // ==================== FINANCE FUNCTIONS ====================
  const fetchFinanceData = async () => {
    setLoadingFinance(true);
    try {
      const [indicatorsRes, trendsRes, profitRes, expensesRes, categoriesRes, wasteRes, areaExpRes] = await Promise.all([
        api.get(`/finances/indicators?period=${financePeriod}&date=${financeDate}`),
        api.get('/finances/trends?months=6'),
        api.get('/finances/profitability-by-product?days=30'),
        api.get('/finances/fixed-expenses'),
        api.get('/finances/expense-categories'),
        api.get('/finances/waste-report?days=30'),
        api.get('/finances/area-expenses?days=30')
      ]);
      setIndicators(indicatorsRes.data);
      setTrends(trendsRes.data);
      setProductProfitability(profitRes.data);
      setFixedExpenses(expensesRes.data);
      setExpenseCategories(categoriesRes.data);
      setWasteReport(wasteRes.data);
      setAreaExpenses(areaExpRes.data);
    } catch (error) {
      toast.error('Error al cargar datos financieros');
    } finally {
      setLoadingFinance(false);
    }
  };

  const handleSaveExpense = async () => {
    if (!expenseForm.category || !expenseForm.name || !expenseForm.amount) {
      return toast.error('Completa los campos obligatorios');
    }

    try {
      if (editingExpense) {
        await api.put(`/finances/fixed-expenses/${editingExpense.expense_id}`, {
          ...expenseForm,
          amount: parseFloat(expenseForm.amount)
        });
        toast.success('Gasto actualizado');
      } else {
        await api.post('/finances/fixed-expenses', {
          ...expenseForm,
          amount: parseFloat(expenseForm.amount)
        });
        toast.success('Gasto agregado');
      }
      setShowExpenseModal(false);
      setEditingExpense(null);
      setExpenseForm({ category: '', name: '', amount: '', is_recurring: true, recurrence_period: 'monthly', notes: '' });
      fetchFinanceData();
    } catch (error) {
      toast.error('Error al guardar gasto');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!confirm('¿Eliminar este gasto fijo?')) return;
    try {
      await api.delete(`/finances/fixed-expenses/${expenseId}`);
      toast.success('Gasto eliminado');
      fetchFinanceData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const openEditExpense = (expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      category: expense.category,
      name: expense.name,
      amount: expense.amount.toString(),
      is_recurring: expense.is_recurring,
      recurrence_period: expense.recurrence_period,
      notes: expense.notes || ''
    });
    setShowExpenseModal(true);
  };

  const totalMonthlyFixed = fixedExpenses.reduce((sum, e) => {
    const amount = e.amount || 0;
    if (e.recurrence_period === 'weekly') return sum + amount * 4.33;
    if (e.recurrence_period === 'yearly') return sum + amount / 12;
    return sum + amount;
  }, 0);

  const ordersByStatus = orders.reduce((acc, order) => {
    acc[order.status] = (acc[order.status] || 0) + 1;
    return acc;
  }, {});

  const statusLabels = {
    pending: 'Pendiente',
    preparing: 'Preparando',
    ready: 'Listo',
    sent: 'Enviado',
    completed: 'Completado',
    cancelled: 'Cancelado'
  };

  const statusColors = {
    pending: '#eab308',
    preparing: '#3b82f6',
    ready: '#22c55e',
    sent: '#a855f7',
    completed: '#64748b',
    cancelled: '#ef4444'
  };

  const completedOrders = orders.filter(o => o.status === 'completed');
  const totalRevenue = completedOrders.reduce((sum, o) => sum + (o.total || 0), 0);
  const avgOrderValue = totalRevenue / Math.max(completedOrders.length, 1);
  const totalItemsSold = topProducts.reduce((sum, p) => sum + (p.quantity || 0), 0);

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-3 md:p-8 pt-16 md:pt-8">
        <div className="max-w-7xl mx-auto" style={{ minHeight: '100vh' }}>
          {/* Header */}
          <div className="flex flex-col gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-chivo font-extrabold" data-testid="reports-title">
                Reportes
              </h1>
              <p className="text-muted-foreground text-sm">Análisis de rendimiento</p>
            </div>
            
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              {/* Tabs */}
              <div className="flex items-center gap-1 bg-muted rounded-lg p-1 flex-1 sm:flex-initial">
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); setActiveTab('general'); }}
                  className={`flex-1 sm:flex-initial px-3 md:px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'general' ? 'bg-white shadow text-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="tab-general"
                >
                  General
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); setActiveTab('customers'); }}
                  className={`flex-1 sm:flex-initial px-3 md:px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    activeTab === 'customers' ? 'bg-white shadow text-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="tab-customers"
                >
                  <Users className="w-4 h-4" />
                  Clientes
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); setActiveTab('finances'); }}
                  className={`flex-1 sm:flex-initial px-3 md:px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    activeTab === 'finances' ? 'bg-white shadow text-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="tab-finances"
                >
                  <Target className="w-4 h-4" />
                  Finanzas
                </button>
              </div>
              
              {activeTab !== 'finances' && (
                <Select value={timeRange} onValueChange={setTimeRange}>
                  <SelectTrigger className="w-full sm:w-[160px]" data-testid="time-range-select">
                    <Calendar className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Período" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 días</SelectItem>
                    <SelectItem value="14">14 días</SelectItem>
                    <SelectItem value="30">30 días</SelectItem>
                    <SelectItem value="90">90 días</SelectItem>
                  </SelectContent>
                </Select>
              )}
              <Button variant="outline" size="icon" onClick={activeTab === 'finances' ? fetchFinanceData : fetchData} disabled={loading || loadingFinance}>
                <RefreshCw className={`w-4 h-4 ${(loading || loadingFinance) ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          {activeTab === 'general' ? (
            <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card className="border border-border/50">
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-orange-500/10">
                    <DollarSign className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="text-xs md:text-sm text-muted-foreground">Ingresos Totales</p>
                    <p className="text-xl md:text-2xl font-chivo font-extrabold">
                      ${totalRevenue.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border border-border/50">
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-green-500/10">
                    <ShoppingBag className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-xs md:text-sm text-muted-foreground">Total Órdenes</p>
                    <p className="text-xl md:text-2xl font-chivo font-extrabold">{orders.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border border-border/50">
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-blue-500/10">
                    <TrendingUp className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-xs md:text-sm text-muted-foreground">Ticket Promedio</p>
                    <p className="text-xl md:text-2xl font-chivo font-extrabold">
                      ${avgOrderValue.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border border-border/50">
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-purple-500/10">
                    <Package className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-xs md:text-sm text-muted-foreground">Items Vendidos</p>
                    <p className="text-xl md:text-2xl font-chivo font-extrabold">{totalItemsSold}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row 1 */}
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            {/* Sales Trend */}
            <Card className="border border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="font-chivo text-lg">Tendencia de Ventas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorVentas" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => [`$${value}`, 'Ventas']}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="ventas" 
                        stroke="#f97316" 
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorVentas)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Orders by Day */}
            <Card className="border border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="font-chivo text-lg">Órdenes por Día</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <Bar dataKey="ordenes" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row 2 */}
          <div className="grid lg:grid-cols-3 gap-6 mb-8">
            {/* Sales by Type Pie */}
            <Card className="border border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="font-chivo text-lg">Ventas por Tipo</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={salesByType}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="total"
                        nameKey="label"
                      >
                        {salesByType.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, '']} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2 mt-2">
                  {salesByType.map((type, idx) => (
                    <div key={type.type} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx] }} />
                        {type.type === 'dine_in' && <Utensils className="w-3 h-3" />}
                        {type.type === 'takeout' && <Package className="w-3 h-3" />}
                        {type.type === 'delivery' && <Truck className="w-3 h-3" />}
                        <span>{type.label}</span>
                      </div>
                      <span className="font-bold">{type.percentage}%</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Category Sales */}
            <Card className="border border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="font-chivo text-lg">Ventas por Categoría</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={categorySales.slice(0, 6)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <YAxis type="category" dataKey="category" stroke="hsl(var(--muted-foreground))" fontSize={11} width={80} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => [`$${value.toLocaleString()}`, 'Ingresos']}
                      />
                      <Bar dataKey="revenue" fill="#6366f1" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Order Status */}
            <Card className="border border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="font-chivo text-lg">Estado de Órdenes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(ordersByStatus).map(([status, count]) => (
                    <div key={status} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: statusColors[status] || '#94a3b8' }} 
                        />
                        <span className="text-sm">{statusLabels[status] || status}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full transition-all"
                            style={{ 
                              width: `${(count / Math.max(orders.length, 1)) * 100}%`,
                              backgroundColor: statusColors[status] || '#94a3b8'
                            }}
                          />
                        </div>
                        <span className="text-sm font-bold w-8 text-right">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
                {orders.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No hay órdenes</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Hourly Sales Chart */}
          <Card className="border border-border/50 mb-8">
            <CardHeader className="pb-2">
              <CardTitle className="font-chivo text-lg">Ventas por Hora (Hoy)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hourlySales.filter(h => h.orders > 0 || h.hour >= '08:00' && h.hour <= '22:00')}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="hour" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      formatter={(value, name) => [name === 'sales' ? `$${value}` : value, name === 'sales' ? 'Ventas' : 'Órdenes']}
                    />
                    <Bar dataKey="orders" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Top Products Table */}
          <Card className="border border-border/50">
            <CardHeader className="pb-2">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="font-chivo text-lg">Productos Más Vendidos</CardTitle>
                  {/* Period Filter Buttons */}
                  <div className="flex gap-1 flex-wrap items-center">
                    {[
                      { value: 'today', label: 'Hoy' },
                      { value: 'week', label: 'Semana' },
                      { value: 'month', label: 'Mes' },
                      { value: 'all', label: 'Todo' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setShowCustomDatePicker(false);
                          setTopProductsPeriod(option.value);
                          setTopProductsCustomStart('');
                          setTopProductsCustomEnd('');
                          fetchTopProducts(option.value);
                        }}
                        className={`px-3 py-1 text-xs rounded-full transition-colors ${
                          topProductsPeriod === option.value
                            ? 'bg-orange-500 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                    
                    {/* Divider */}
                    <div className="h-5 w-px bg-slate-300 mx-1" />
                    
                    {/* Month Selector */}
                    <select
                      value={topProductsPeriod === 'month-specific' ? topProductsSelectedMonth : ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          const [year, month] = e.target.value.split('-').map(Number);
                          const startDate = new Date(year, month - 1, 1);
                          const endDate = new Date(year, month, 0);
                          setTopProductsSelectedMonth(e.target.value);
                          setTopProductsPeriod('month-specific');
                          setShowCustomDatePicker(false);
                          fetchTopProducts('custom', startDate.toISOString().split('T')[0], endDate.toISOString().split('T')[0]);
                        }
                      }}
                      className={`px-2 py-1 text-xs border rounded-full ${
                        topProductsPeriod === 'month-specific' 
                          ? 'bg-orange-500 text-white border-orange-500' 
                          : 'bg-white border-slate-300'
                      }`}
                    >
                      <option value="">Mes específico</option>
                      {getMonthOptions().map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                    
                    {/* Custom button */}
                    <button
                      onClick={() => {
                        const yesterday = new Date();
                        yesterday.setDate(yesterday.getDate() - 1);
                        const yesterdayStr = yesterday.toISOString().split('T')[0];
                        setTopProductsCustomStart(yesterdayStr);
                        setTopProductsCustomEnd(yesterdayStr);
                        setShowCustomDatePicker(true);
                        setTopProductsPeriod('custom');
                      }}
                      className={`px-3 py-1 text-xs rounded-full transition-colors ${
                        topProductsPeriod === 'custom'
                          ? 'bg-orange-500 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      Personalizado
                    </button>
                  </div>
                </div>
                
                {/* Period Indicator */}
                <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-lg w-fit">
                  <Calendar className="w-4 h-4 text-orange-600" />
                  <span className="text-sm font-medium text-orange-800">
                    📊 Mostrando: {getTopProductsPeriodLabel()}
                  </span>
                </div>
                
                {/* Custom Date Range Picker */}
                {showCustomDatePicker && (
                  <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-slate-600">Desde:</span>
                      <input
                        type="date"
                        value={topProductsCustomStart}
                        onChange={(e) => setTopProductsCustomStart(e.target.value)}
                        className="px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                      <span className="text-sm text-slate-600">Hasta:</span>
                      <input
                        type="date"
                        value={topProductsCustomEnd}
                        onChange={(e) => setTopProductsCustomEnd(e.target.value)}
                        className="px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                      <Button
                        size="sm"
                        disabled={loadingTopProducts}
                        onClick={() => {
                          if (topProductsCustomStart && topProductsCustomEnd) {
                            fetchTopProducts('custom', topProductsCustomStart, topProductsCustomEnd);
                          } else {
                            toast.error('Selecciona ambas fechas');
                          }
                        }}
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                      >
                        {loadingTopProducts ? 'Buscando...' : 'Buscar'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Producto</TableHead>
                    <TableHead className="text-right">Cantidad</TableHead>
                    <TableHead className="text-right">Ingresos</TableHead>
                    <TableHead className="text-right">% del Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topProducts.map((product, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <span className="w-6 h-6 rounded-full bg-orange-500/10 flex items-center justify-center text-orange-500 font-bold text-sm">
                          {idx + 1}
                        </span>
                      </TableCell>
                      <TableCell className="font-medium">{product.name}</TableCell>
                      <TableCell className="text-right">{product.quantity}</TableCell>
                      <TableCell className="text-right font-bold text-green-600">
                        ${product.revenue?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {totalRevenue > 0 ? ((product.revenue / totalRevenue) * 100).toFixed(1) : 0}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {topProducts.length === 0 && (
                <p className="text-center text-muted-foreground py-8">No hay datos de ventas</p>
              )}
            </CardContent>
          </Card>
            </>
          ) : activeTab === 'customers' ? (
            /* Customer Report Tab - with Side Panel */
            <div className="flex gap-6">
              {/* Left: Stats + Customer List */}
              <div className={`space-y-6 transition-all duration-300 ${selectedCustomer ? 'w-1/2' : 'w-full'}`}>
                {/* Customer Summary */}
                <div className={`grid ${selectedCustomer ? 'grid-cols-2' : 'grid-cols-2 lg:grid-cols-4'} gap-4`}>
                  <Card className="border border-border/50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-3 rounded-lg bg-blue-500/10">
                          <Users className="w-5 h-5 text-blue-500" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Clientes</p>
                          <p className="text-2xl font-chivo font-extrabold">{customerReport.length}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border border-border/50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-3 rounded-lg bg-green-500/10">
                          <DollarSign className="w-5 h-5 text-green-500" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Total</p>
                          <p className="text-2xl font-chivo font-extrabold">
                            ${customerReport.reduce((sum, c) => sum + c.total_spent, 0).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  {!selectedCustomer && (
                    <>
                      <Card className="border border-border/50">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="p-3 rounded-lg bg-orange-500/10">
                              <ShoppingBag className="w-5 h-5 text-orange-500" />
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Total Órdenes</p>
                              <p className="text-2xl font-chivo font-extrabold">
                                {customerReport.reduce((sum, c) => sum + c.orders_count, 0)}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card className="border border-border/50">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="p-3 rounded-lg bg-purple-500/10">
                              <TrendingUp className="w-5 h-5 text-purple-500" />
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Ticket Promedio</p>
                              <p className="text-2xl font-chivo font-extrabold">
                                ${(customerReport.reduce((sum, c) => sum + c.total_spent, 0) / Math.max(customerReport.reduce((sum, c) => sum + c.orders_count, 0), 1)).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </>
                  )}
                </div>

                {/* Customers List */}
                <Card className="border border-border/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="font-chivo text-base flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Clientes del Período
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y max-h-[calc(100vh-400px)] overflow-y-auto">
                      {customerReport.map((customer, idx) => {
                        const isSelected = selectedCustomer?.name === customer.name && selectedCustomer?.phone === customer.phone;
                        return (
                          <div
                            key={idx}
                            onClick={() => setSelectedCustomer(isSelected ? null : customer)}
                            className={`p-4 cursor-pointer transition-all hover:bg-gray-50 ${
                              isSelected ? 'bg-accent/10 border-l-4 border-l-accent' : ''
                            }`}
                            data-testid={`customer-report-row-${idx}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                  idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-orange-600' : isSelected ? 'bg-accent' : 'bg-gray-200'
                                } text-white font-bold`}>
                                  {idx < 3 ? idx + 1 : <Users className="w-5 h-5" />}
                                </div>
                                <div>
                                  <p className="font-medium">{customer.name}</p>
                                  <p className="text-sm text-muted-foreground">
                                    {customer.phone || 'Sin teléfono'}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-green-600">
                                  ${customer.total_spent.toLocaleString(undefined, {minimumFractionDigits: 2})}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {customer.orders_count} órdenes
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {customerReport.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">No hay datos de clientes</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Right: Customer Detail Panel (FUDO style) */}
              {selectedCustomer && (
                <div className="w-1/2 bg-slate-800 rounded-xl text-white overflow-hidden flex flex-col max-h-[calc(100vh-200px)]">
                  {/* Panel Header */}
                  <div className="bg-slate-900 p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center">
                        <Users className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg">{selectedCustomer.name}</h3>
                        <p className="text-slate-400 text-sm flex items-center gap-2">
                          {selectedCustomer.phone && (
                            <>
                              <Phone className="w-3 h-3" />
                              {selectedCustomer.phone}
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                    <button 
                      onClick={() => setSelectedCustomer(null)}
                      className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Customer Stats */}
                  <div className="grid grid-cols-3 gap-3 p-4 bg-slate-900/50">
                    <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-green-400">
                        ${selectedCustomer.total_spent.toLocaleString(undefined, {minimumFractionDigits: 0})}
                      </p>
                      <p className="text-xs text-slate-400">Total Gastado</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-accent">{selectedCustomer.orders_count}</p>
                      <p className="text-xs text-slate-400">Órdenes</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-blue-400">
                        ${selectedCustomer.avg_ticket.toLocaleString(undefined, {minimumFractionDigits: 0})}
                      </p>
                      <p className="text-xs text-slate-400">Ticket Promedio</p>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {/* Favorite Products */}
                    <div>
                      <h4 className="text-sm font-medium text-slate-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                        <Heart className="w-4 h-4" />
                        Productos Favoritos
                      </h4>
                      {selectedCustomer.favorite_products?.length > 0 ? (
                        <div className="space-y-2">
                          {selectedCustomer.favorite_products.map((prod, idx) => (
                            <div key={idx} className="bg-slate-700/50 rounded-lg p-3 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="w-6 h-6 bg-accent/20 text-accent rounded-full flex items-center justify-center text-sm font-bold">
                                  {idx + 1}
                                </span>
                                <span className="text-sm">{prod.name}</span>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-green-400">${prod.total?.toLocaleString() || '0'}</p>
                                <p className="text-xs text-slate-400">{prod.count}x</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-center py-3 bg-slate-700/30 rounded-lg text-sm">Sin datos</p>
                      )}
                    </div>

                    {/* Last Visit */}
                    <div className="bg-slate-700/50 rounded-lg p-4">
                      <p className="text-xs text-slate-400 mb-1">Última Visita</p>
                      <p className="text-lg font-medium">
                        {selectedCustomer.last_visit ? new Date(selectedCustomer.last_visit).toLocaleDateString('es-MX', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        }) : 'Sin registro'}
                      </p>
                    </div>

                    {/* Marketing Insight */}
                    {selectedCustomer.favorite_products?.length > 0 && (
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                        <p className="text-sm text-blue-300">
                          <strong>💡 Insight:</strong> Este cliente prefiere{' '}
                          <strong>{selectedCustomer.favorite_products[0]?.name}</strong>. 
                          Ha ordenado {selectedCustomer.orders_count} veces con un ticket promedio de ${selectedCustomer.avg_ticket.toFixed(0)}.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {/* ==================== FINANCES TAB ==================== */}
          {activeTab === 'finances' && (
            <div className="space-y-6">
              {/* Period Selector */}
              <div className="flex flex-wrap items-center gap-3 bg-slate-50 rounded-lg p-3">
                <div className="flex items-center gap-1 bg-white rounded-lg p-1 shadow-sm">
                  {['day', 'week', 'month', 'year'].map((p) => (
                    <button
                      key={p}
                      onClick={() => setFinancePeriod(p)}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        financePeriod === p ? 'bg-accent text-white' : 'text-muted-foreground hover:bg-slate-100'
                      }`}
                    >
                      {p === 'day' ? 'Día' : p === 'week' ? 'Semana' : p === 'month' ? 'Mes' : 'Año'}
                    </button>
                  ))}
                </div>
                <Input
                  type="date"
                  value={financeDate}
                  onChange={(e) => setFinanceDate(e.target.value)}
                  className="w-auto"
                />
                {loadingFinance && <RefreshCw className="w-4 h-4 animate-spin text-muted-foreground" />}
              </div>

              {indicators && (
                <>
                  {/* Break-Even Status Banner */}
                  <Card className={`border-2 ${indicators.is_profitable ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-16 h-16 rounded-full flex items-center justify-center ${indicators.is_profitable ? 'bg-green-500' : 'bg-red-500'}`}>
                            {indicators.is_profitable ? (
                              <CheckCircle className="w-8 h-8 text-white" />
                            ) : (
                              <AlertTriangle className="w-8 h-8 text-white" />
                            )}
                          </div>
                          <div>
                            <h3 className="text-lg font-bold">
                              {indicators.is_profitable ? '¡Operación Rentable!' : 'Por debajo del Punto de Equilibrio'}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {indicators.period_label} • {indicators.total_orders} órdenes
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-center">
                            <p className="text-xs text-muted-foreground">Punto de Equilibrio</p>
                            <p className="text-xl font-bold">${indicators.break_even_sales.toLocaleString()}</p>
                          </div>
                          <ChevronRight className="w-6 h-6 text-muted-foreground" />
                          <div className="text-center">
                            <p className="text-xs text-muted-foreground">Ventas Actuales</p>
                            <p className={`text-xl font-bold ${indicators.is_profitable ? 'text-green-600' : 'text-red-600'}`}>
                              ${indicators.total_sales.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                      {/* Progress Bar */}
                      <div className="mt-4">
                        <div className="flex justify-between text-xs mb-1">
                          <span>Progreso hacia el equilibrio</span>
                          <span className="font-medium">{indicators.progress_to_break_even}%</span>
                        </div>
                        <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all ${indicators.progress_to_break_even >= 100 ? 'bg-green-500' : 'bg-amber-500'}`}
                            style={{ width: `${Math.min(indicators.progress_to_break_even, 100)}%` }}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Key Metrics Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="border-l-4 border-l-green-500">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <DollarSign className="w-4 h-4 text-green-500" />
                          <span className="text-xs text-muted-foreground">Ventas Totales</span>
                        </div>
                        <p className="text-2xl font-bold">${indicators.total_sales.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground">{indicators.total_orders} órdenes</p>
                      </CardContent>
                    </Card>
                    <Card className="border-l-4 border-l-red-500">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Package className="w-4 h-4 text-red-500" />
                          <span className="text-xs text-muted-foreground">Costos Variables</span>
                        </div>
                        <p className="text-2xl font-bold">${indicators.variable_costs.toLocaleString()}</p>
                        <p className={`text-xs ${indicators.food_cost_status === 'optimal' ? 'text-green-600' : indicators.food_cost_status === 'warning' ? 'text-amber-600' : 'text-red-600'}`}>
                          {indicators.food_cost_pct}% food cost
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="border-l-4 border-l-orange-500">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Building2 className="w-4 h-4 text-orange-500" />
                          <span className="text-xs text-muted-foreground">Gastos Fijos</span>
                        </div>
                        <p className="text-2xl font-bold">${indicators.total_fixed.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground">prorrateado al período</p>
                      </CardContent>
                    </Card>
                    <Card className={`border-l-4 ${indicators.net_profit >= 0 ? 'border-l-blue-500' : 'border-l-red-500'}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <PiggyBank className={`w-4 h-4 ${indicators.net_profit >= 0 ? 'text-blue-500' : 'text-red-500'}`} />
                          <span className="text-xs text-muted-foreground">Utilidad Neta</span>
                        </div>
                        <p className={`text-2xl font-bold ${indicators.net_profit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                          ${indicators.net_profit.toLocaleString()}
                        </p>
                        <p className="text-xs text-muted-foreground">{indicators.net_margin_pct}% margen neto</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Cost Breakdown Card */}
                  {indicators.cost_breakdown && (
                    <Card className="bg-slate-50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <Calculator className="w-4 h-4 text-red-500" />
                          Desglose de Costos Variables
                          {indicators.variable_cost_source && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              indicators.variable_cost_source === 'inventory' ? 'bg-green-100 text-green-700' :
                              indicators.variable_cost_source === 'recipes' ? 'bg-blue-100 text-blue-700' :
                              'bg-amber-100 text-amber-700'
                            }`}>
                              {indicators.variable_cost_source === 'inventory' ? 'Inventario Real' :
                               indicators.variable_cost_source === 'recipes' ? 'Recetas' : 'Estimado'}
                            </span>
                          )}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="p-3 bg-white rounded-lg border">
                            <p className="text-xs text-muted-foreground mb-1">Costo de Recetas</p>
                            <p className="font-semibold">${indicators.cost_breakdown.recipe_costs?.toLocaleString() || 0}</p>
                            <p className="text-xs text-slate-500">teórico</p>
                          </div>
                          <div className="p-3 bg-white rounded-lg border">
                            <p className="text-xs text-muted-foreground mb-1">Consumo Real</p>
                            <p className="font-semibold text-blue-600">${indicators.cost_breakdown.real_consumption?.toLocaleString() || 0}</p>
                            <p className="text-xs text-slate-500">inventario usado</p>
                          </div>
                          <div className="p-3 bg-white rounded-lg border">
                            <p className="text-xs text-muted-foreground mb-1">Mermas/Pérdidas</p>
                            <p className="font-semibold text-red-600">${indicators.cost_breakdown.waste_losses?.toLocaleString() || 0}</p>
                            <p className="text-xs text-slate-500">ajustes negativos</p>
                          </div>
                          <div className="p-3 bg-white rounded-lg border">
                            <p className="text-xs text-muted-foreground mb-1">Compras del Período</p>
                            <p className="font-semibold text-orange-600">${indicators.cost_breakdown.purchases_period?.toLocaleString() || 0}</p>
                            <p className="text-xs text-slate-500">recibidas</p>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-3 italic">
                          * Los costos variables se calculan usando: {
                            indicators.variable_cost_source === 'inventory' ? 'deducciones reales de inventario + mermas' :
                            indicators.variable_cost_source === 'recipes' ? 'costos de receta por venta + mermas' :
                            'estimación basada en compras (35%)'
                          }
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Charts Row */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Trend Chart */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-accent" />
                          Tendencia Financiera (6 meses)
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={trends}>
                              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                              <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
                              <Legend />
                              <Area type="monotone" dataKey="sales" name="Ventas" stroke={CHART_COLORS.sales} fill={CHART_COLORS.sales} fillOpacity={0.3} />
                              <Area type="monotone" dataKey="net_profit" name="Utilidad" stroke={CHART_COLORS.profit} fill={CHART_COLORS.profit} fillOpacity={0.3} />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Break-Even Chart */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <Target className="w-4 h-4 text-accent" />
                          Ventas vs Punto de Equilibrio
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={[
                              { name: 'Punto Equilibrio', value: indicators.break_even_sales, fill: '#94a3b8' },
                              { name: 'Ventas Actuales', value: indicators.total_sales, fill: indicators.is_profitable ? '#10b981' : '#ef4444' }
                            ]}>
                              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                              <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
                              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                                {[0, 1].map((_, idx) => (
                                  <Cell key={idx} fill={idx === 0 ? '#94a3b8' : (indicators.is_profitable ? '#10b981' : '#ef4444')} />
                                ))}
                              </Bar>
                              <ReferenceLine y={indicators.break_even_sales} stroke="#f97316" strokeDasharray="5 5" label="P.E." />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Fixed Expenses & Product Profitability */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Fixed Expenses */}
                    <Card>
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-sm font-medium flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-orange-500" />
                            Gastos Fijos Mensuales
                          </CardTitle>
                          <Button size="sm" onClick={() => { setEditingExpense(null); setExpenseForm({ category: '', name: '', amount: '', is_recurring: true, recurrence_period: 'monthly', notes: '' }); setShowExpenseModal(true); }}>
                            <Plus className="w-4 h-4 mr-1" />
                            Agregar
                          </Button>
                        </div>
                        <p className="text-2xl font-bold text-orange-600">${totalMonthlyFixed.toLocaleString()}/mes</p>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 max-h-64 overflow-auto">
                          {fixedExpenses.length === 0 ? (
                            <p className="text-center text-muted-foreground py-4">No hay gastos fijos registrados</p>
                          ) : (
                            fixedExpenses.map((expense) => (
                              <div key={expense.expense_id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg group">
                                <div className="flex-1">
                                  <p className="font-medium text-sm">{expense.name}</p>
                                  <p className="text-xs text-muted-foreground">{expense.category}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="font-bold">${expense.amount.toLocaleString()}</span>
                                  <span className="text-xs text-muted-foreground">/{expense.recurrence_period === 'monthly' ? 'mes' : expense.recurrence_period === 'weekly' ? 'sem' : 'año'}</span>
                                  <button onClick={() => openEditExpense(expense)} className="p-1 opacity-0 group-hover:opacity-100 hover:bg-slate-200 rounded">
                                    <Edit className="w-3 h-3" />
                                  </button>
                                  <button onClick={() => handleDeleteExpense(expense.expense_id)} className="p-1 opacity-0 group-hover:opacity-100 hover:bg-red-100 rounded text-red-500">
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Product Profitability */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <Calculator className="w-4 h-4 text-blue-500" />
                          Rentabilidad por Producto (30 días)
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 max-h-64 overflow-auto">
                          {productProfitability.slice(0, 10).map((product, idx) => (
                            <div key={product.product_id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                              <div className="flex items-center gap-2 flex-1">
                                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                  idx < 3 ? 'bg-green-500 text-white' : 'bg-slate-200'
                                }`}>
                                  {idx + 1}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">{product.product_name}</p>
                                  <p className="text-xs text-muted-foreground">{product.units_sold} vendidos</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-green-600">${product.profit.toLocaleString()}</p>
                                <p className={`text-xs ${product.margin_pct >= 60 ? 'text-green-600' : product.margin_pct >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                                  {product.margin_pct}% margen
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Food Cost Alert */}
                  {indicators.food_cost_status !== 'optimal' && (
                    <Card className={`border ${indicators.food_cost_status === 'warning' ? 'border-amber-500 bg-amber-50' : 'border-red-500 bg-red-50'}`}>
                      <CardContent className="p-4 flex items-center gap-4">
                        <AlertTriangle className={`w-8 h-8 ${indicators.food_cost_status === 'warning' ? 'text-amber-500' : 'text-red-500'}`} />
                        <div>
                          <h4 className="font-bold">Costo de Alimentos: {indicators.food_cost_pct}%</h4>
                          <p className="text-sm">
                            {indicators.food_cost_status === 'warning' 
                              ? 'Tu costo de alimentos está por encima del óptimo (28-35%). Revisa tus recetas y proveedores.'
                              : '¡Alerta! Tu costo de alimentos supera el 40%. Es urgente optimizar compras y reducir mermas.'
                            }
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* ==================== WASTE REPORT SECTION ==================== */}
                  {wasteReport && (
                    <Card className="border-t-4 border-t-red-500">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg font-bold flex items-center gap-2">
                          <Flame className="w-5 h-5 text-red-500" />
                          Reporte de Mermas (30 días)
                        </CardTitle>
                        <p className="text-sm text-muted-foreground">
                          Identifica los insumos con mayor desperdicio para optimizar costos
                        </p>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {/* Summary Cards */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                            <p className="text-xs text-red-600 mb-1">Pérdida Total</p>
                            <p className="text-xl font-bold text-red-700">${wasteReport.summary.total_waste_value.toLocaleString()}</p>
                          </div>
                          <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                            <p className="text-xs text-amber-600 mb-1">% de Compras</p>
                            <p className="text-xl font-bold text-amber-700">{wasteReport.summary.waste_percentage}%</p>
                            <p className="text-xs text-amber-500">Estándar: 2-3%</p>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-lg border">
                            <p className="text-xs text-muted-foreground mb-1">Insumos Afectados</p>
                            <p className="text-xl font-bold">{wasteReport.summary.unique_supplies_affected}</p>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-lg border">
                            <p className="text-xs text-muted-foreground mb-1">Promedio Diario</p>
                            <p className="text-xl font-bold">${wasteReport.summary.avg_daily_waste.toLocaleString()}</p>
                          </div>
                        </div>

                        {/* Recommendations */}
                        {wasteReport.recommendations && wasteReport.recommendations.length > 0 && (
                          <div className="space-y-2">
                            <h4 className="font-semibold flex items-center gap-2 text-sm">
                              <Lightbulb className="w-4 h-4 text-amber-500" />
                              Recomendaciones
                            </h4>
                            {wasteReport.recommendations.map((rec, idx) => (
                              <div key={idx} className={`p-3 rounded-lg border-l-4 ${
                                rec.type === 'critical' ? 'bg-red-50 border-red-500' :
                                rec.type === 'warning' ? 'bg-amber-50 border-amber-500' :
                                'bg-blue-50 border-blue-500'
                              }`}>
                                <p className="font-medium text-sm">{rec.title}</p>
                                <p className="text-xs text-muted-foreground mt-1">{rec.description}</p>
                                {rec.potential_savings > 0 && (
                                  <p className="text-xs text-green-600 mt-1 font-medium">
                                    Ahorro potencial: ${rec.potential_savings.toLocaleString()}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          {/* Top Waste by Supply */}
                          <div>
                            <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                              <Package className="w-4 h-4 text-red-500" />
                              Top Insumos con Mayor Merma
                            </h4>
                            {wasteReport.top_waste_supplies.length === 0 ? (
                              <p className="text-center text-muted-foreground py-4 text-sm">
                                No hay mermas registradas en este período
                              </p>
                            ) : (
                              <div className="space-y-2 max-h-64 overflow-auto">
                                {wasteReport.top_waste_supplies.slice(0, 8).map((item, idx) => (
                                  <div key={item.supply_id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                                    <div className="flex items-center gap-2">
                                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                        idx === 0 ? 'bg-red-500 text-white' :
                                        idx === 1 ? 'bg-red-400 text-white' :
                                        idx === 2 ? 'bg-red-300 text-white' :
                                        'bg-slate-200'
                                      }`}>
                                        {idx + 1}
                                      </span>
                                      <div>
                                        <p className="font-medium text-sm">{item.supply_name}</p>
                                        <p className="text-xs text-muted-foreground">
                                          {item.total_qty} {item.unit} • {item.occurrence_count} ajustes
                                        </p>
                                      </div>
                                    </div>
                                    <p className="font-bold text-red-600">${item.total_value.toLocaleString()}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Waste by Reason */}
                          <div>
                            <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4 text-amber-500" />
                              Merma por Motivo
                            </h4>
                            {wasteReport.by_reason.length === 0 ? (
                              <p className="text-center text-muted-foreground py-4 text-sm">
                                Sin datos de motivos
                              </p>
                            ) : (
                              <div className="h-48">
                                <ResponsiveContainer width="100%" height="100%">
                                  <PieChart>
                                    <Pie
                                      data={wasteReport.by_reason}
                                      dataKey="total_value"
                                      nameKey="reason_label"
                                      cx="50%"
                                      cy="50%"
                                      outerRadius={60}
                                      label={({ reason_label, percent }) => `${reason_label} ${(percent * 100).toFixed(0)}%`}
                                      labelLine={false}
                                    >
                                      {wasteReport.by_reason.map((_, index) => (
                                        <Cell key={index} fill={['#ef4444', '#f97316', '#eab308', '#6366f1', '#94a3b8'][index % 5]} />
                                      ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                                  </PieChart>
                                </ResponsiveContainer>
                              </div>
                            )}

                            {/* By Area */}
                            {wasteReport.by_area.length > 0 && (
                              <div className="mt-4">
                                <h5 className="font-medium text-xs mb-2 text-muted-foreground">Por Área:</h5>
                                <div className="flex flex-wrap gap-2">
                                  {wasteReport.by_area.map((area) => (
                                    <span key={area.area} className="px-2 py-1 bg-slate-100 rounded text-xs">
                                      {area.area}: <span className="font-bold text-red-600">${area.total_value.toLocaleString()}</span>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Daily Trend Chart */}
                        {wasteReport.daily_trend && wasteReport.daily_trend.some(d => d.value > 0) && (
                          <div>
                            <h4 className="font-semibold text-sm mb-3">Tendencia Diaria de Mermas</h4>
                            <div className="h-32">
                              <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={wasteReport.daily_trend}>
                                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v}`} />
                                  <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
                                  <Area type="monotone" dataKey="value" name="Merma" stroke="#ef4444" fill="#fecaca" />
                                </AreaChart>
                              </ResponsiveContainer>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* ==================== AREA EXPENSES (CONSUMABLES) SECTION ==================== */}
                  {areaExpenses && (
                    <Card className="border-t-4 border-t-amber-500">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg font-bold flex items-center gap-2">
                          <Package className="w-5 h-5 text-amber-500" />
                          Gastos por Área - Consumibles (30 días)
                        </CardTitle>
                        <p className="text-sm text-muted-foreground">
                          Gastos de insumos consumibles (servilletas, bolsas, desechables) por área
                        </p>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {/* Summary Cards */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                            <p className="text-xs text-amber-600 mb-1">Gasto Total</p>
                            <p className="text-xl font-bold text-amber-700">${areaExpenses.summary.total_expenses.toLocaleString()}</p>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-lg border">
                            <p className="text-xs text-muted-foreground mb-1">Áreas</p>
                            <p className="text-xl font-bold">{areaExpenses.summary.total_areas}</p>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-lg border">
                            <p className="text-xs text-muted-foreground mb-1">Insumos</p>
                            <p className="text-xl font-bold">{areaExpenses.summary.total_supplies}</p>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-lg border">
                            <p className="text-xs text-muted-foreground mb-1">Promedio Diario</p>
                            <p className="text-xl font-bold">${areaExpenses.summary.avg_daily.toLocaleString()}</p>
                          </div>
                        </div>

                        {areaExpenses.by_area.length === 0 ? (
                          <div className="text-center py-8 text-muted-foreground">
                            <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                            <p>No hay gastos de consumibles registrados</p>
                            <p className="text-sm mt-1">Marca insumos como "Consumible" y haz traspasos a las áreas</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {/* Areas with their expenses */}
                            {areaExpenses.by_area.map((area) => (
                              <div key={area.area_id} className="border rounded-lg overflow-hidden">
                                <div className="bg-slate-100 px-4 py-3 flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold text-sm">
                                      {area.area_name.charAt(0)}
                                    </div>
                                    <div>
                                      <h4 className="font-bold">{area.area_name}</h4>
                                      <p className="text-xs text-muted-foreground">{area.items_count} traspasos</p>
                                    </div>
                                  </div>
                                  <p className="text-xl font-bold text-amber-600">${area.total_cost.toLocaleString()}</p>
                                </div>
                                {area.top_supplies.length > 0 && (
                                  <div className="p-3 space-y-2">
                                    {area.top_supplies.slice(0, 5).map((supply) => (
                                      <div key={supply.supply_id} className="flex items-center justify-between text-sm">
                                        <span className="text-muted-foreground">{supply.supply_name}</span>
                                        <div className="flex items-center gap-3">
                                          <span className="text-xs text-slate-400">{supply.total_qty} {supply.unit}</span>
                                          <span className="font-medium">${supply.total_cost.toLocaleString()}</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Top Supplies Chart */}
                        {areaExpenses.top_supplies.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-sm mb-3">Top Consumibles por Gasto</h4>
                            <div className="h-48">
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={areaExpenses.top_supplies.slice(0, 8)} layout="vertical">
                                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                  <XAxis type="number" tickFormatter={(v) => `$${v}`} />
                                  <YAxis type="category" dataKey="supply_name" width={100} tick={{ fontSize: 11 }} />
                                  <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
                                  <Bar dataKey="total_cost" name="Gasto" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                                </BarChart>
                              </ResponsiveContainer>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </main>

      {/* ==================== EXPENSE MODAL ==================== */}
      {showExpenseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{editingExpense ? 'Editar Gasto Fijo' : 'Nuevo Gasto Fijo'}</CardTitle>
                <button onClick={() => setShowExpenseModal(false)} className="p-1 hover:bg-slate-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Categoría *</Label>
                <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm({...expenseForm, category: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {expenseCategories.map((cat) => (
                      <SelectItem key={cat.category_id} value={cat.name}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Nombre del gasto *</Label>
                <Input 
                  value={expenseForm.name} 
                  onChange={(e) => setExpenseForm({...expenseForm, name: e.target.value})}
                  placeholder="Ej: Renta local, Luz CFE"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Monto *</Label>
                  <Input 
                    type="number"
                    value={expenseForm.amount} 
                    onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label>Frecuencia</Label>
                  <Select value={expenseForm.recurrence_period} onValueChange={(v) => setExpenseForm({...expenseForm, recurrence_period: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Semanal</SelectItem>
                      <SelectItem value="monthly">Mensual</SelectItem>
                      <SelectItem value="yearly">Anual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Notas (opcional)</Label>
                <Input 
                  value={expenseForm.notes} 
                  onChange={(e) => setExpenseForm({...expenseForm, notes: e.target.value})}
                  placeholder="Observaciones"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowExpenseModal(false)}>Cancelar</Button>
                <Button className="flex-1" onClick={handleSaveExpense}>Guardar</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Reports;
